import clsx from "clsx";

import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { KEYBOARD_TAB_INDEX } from "@/utils/keyboardTabIndex";

import { getNavItemStateClasses } from "./navItemStateClasses";

type NavItemButtonProps = {
  buttonHandler: () => void;
  label: string;
  selected: boolean;
  /** Use the inverted (light-on-dark) variant. Default: false. */
  inverted?: boolean;
  /** Disables the item. Default: false. */
  disabled?: boolean;
};

/**
 * Button nav item, e.g. a language in the footer's Collapsible.
 * Implements all states (default, hovered, pressed, focused, disabled)
 * and both variants (default, inverted).
 *
 * @example
 * ```jsx
 * <NavItemButton
 *   buttonHandler={handleClick}
 *   selected={false}
 *   label="My Label"
 *   inverted
 * />
 * ```
 *
 * @param buttonHandler - Called when the button is clicked.
 * @param label - Unique label text displayed in the menu.
 * @param selected - Whether the item is in the current/selected state.
 * @param inverted - Use the inverted (light-on-dark) variant. Default: false.
 * @param disabled - Disables the item. Default: false.
 */
export const NavItemButton: React.FC<NavItemButtonProps> = ({
  label,
  buttonHandler,
  selected,
  inverted = false,
  disabled = false,
}) => (
  <button
    id={label.toLowerCase().replace(/\s+/g, "-")}
    className={clsx(
      "group relative w-fit cursor-pointer rounded-md p-2 text-body-base font-semibold focus:outline-none",
      getNavItemStateClasses({ inverted, disabled, selected }),
      disabled && "cursor-not-allowed",
    )}
    type="button"
    tabIndex={disabled ? -1 : KEYBOARD_TAB_INDEX}
    aria-current={selected ? "true" : undefined}
    onClick={buttonHandler}
    disabled={disabled}
  >
    {label}
    <FocusRing inverted={inverted} />
  </button>
);
